"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { TOCItem } from "../types";

interface TableOfContentsProps {
	items: TOCItem[];
}

export const TableOfContents = ({ items }: TableOfContentsProps) => {
	const [activeSection, setActiveSection] = useState<string>("");

	// Effect to add IDs to headings and track active section with Intersection Observer
	useEffect(() => {
		if (items.length === 0) {
			return;
		}

		// Add IDs to heading elements
		const addHeadingIds = () => {
			const headingElements = document.querySelectorAll(
				".rnr-container h1, .rnr-container h2, .rnr-container h3",
			);
			const tocMap = new Map();

			// Create a map of heading text to TOC items
			for (const item of items) {
				tocMap.set(item.text.toLowerCase().trim(), item.id);
			}

			for (const heading of headingElements) {
				const text = heading.textContent?.toLowerCase().trim();
				if (text && tocMap.has(text)) {
					heading.id = tocMap.get(text);
				}
			}
		};

		// Add IDs after a short delay to ensure content is rendered
		const timeoutId = setTimeout(addHeadingIds, 100);

		// Set up Intersection Observer
		const observerOptions = {
			rootMargin: "-100px 0px -60% 0px",
			threshold: 0,
		};

		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && entry.target.id) {
					setActiveSection(entry.target.id);
				}
			}
		}, observerOptions);

		// Observe heading elements after IDs are added
		const observeHeadings = () => {
			const headingElements = document.querySelectorAll(
				".rnr-container h1[id], .rnr-container h2[id], .rnr-container h3[id]",
			);
			for (const heading of headingElements) {
				observer.observe(heading);
			}
		};

		const observeTimeoutId = setTimeout(observeHeadings, 200);

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(observeTimeoutId);
			observer.disconnect();
		};
	}, [items]);

	if (items.length === 0) {
		return null;
	}

	return (
		<div>
			<h3 className="mb-3 font-medium text-gray-900 text-sm">
				Table of Contents
			</h3>
			<div className="border-gray-200 border-t pt-3">
				<nav className="space-y-1">
					{items.map((item) => (
						<Link
							key={item.id}
							href={`#${item.id}`}
							scroll={false}
							onClick={(e) => {
								e.preventDefault();
								document.getElementById(item.id)?.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}}
							className={`block rounded px-2 py-1.5 text-sm transition-all duration-200 ${
								item.level === 1
									? "font-medium text-gray-900"
									: item.level === 2
										? "pl-4 text-gray-700"
										: "pl-6 text-gray-600"
							} ${
								activeSection === item.id
									? "bg-primary/10 font-semibold text-primary"
									: "hover:bg-gray-50 hover:text-primary"
							}`}
						>
							{item.text}
						</Link>
					))}
				</nav>
			</div>
		</div>
	);
};
